import type {
  CanvasHeaderFooterSection,
  CanvasHeaderFooterType,
  CanvasHeaderFooterZone,
  CanvasHeadersFooters,
} from '@hector/shared-types';
import type { SuperDocDocumentApi, SuperDocDocumentMethod } from './superDocDocumentApi';
import { lireLaRevisionDuDocument, lireLInfoDuDocument, REVISION_ABSENTE, type RevisionDeDocument } from './fencesDeMutation';
import { lireLeCorpsSdm, lireLesAttributsStructurels, type AttributsStructurels } from './lectureSdm';

/**
 * Borne HAUTE du texte publié au modèle, par bloc (#1008).
 *
 * ⚠️ Ce n'est pas un aperçu : c'est le texte du bloc, tronqué seulement s'il
 * dépasse cette borne — et la troncature est alors DÉCLARÉE (`textTruncated`),
 * parce qu'un texte coupé en silence se lit comme un texte complet et fait
 * conclure de travers sur ce que le paragraphe contient.
 *
 * **Le coût de contexte a été CHIFFRÉ avant d'être écarté** (relevé du
 * 2026-08-16, registre d'audit `2026-08-16-audit-conversations-canvas.md`) : un
 * acte de 28 pages / 852 blocs = 73 318 caractères ≈ 18 000 jetons, et la
 * fenêtre par défaut de 80 blocs ≈ 1 700 jetons. Le bornage utile est le
 * FENÊTRAGE (`blockOffset` / `blockLimit`, plafond 250), pas la mutilation de
 * chaque bloc : servir 78 caractères par paragraphe économisait des jetons en
 * rendant l'agent incapable de situer quoi que ce soit dans l'acte — il a
 * dépensé six inspections ciblées puis renoncé (conv 9dd5b87e, prod).
 *
 * 2 000 caractères ≈ 500 jetons : au-delà, un seul bloc pathologique (une page
 * entière en un paragraphe) mangerait la fenêtre à lui seul. La valeur est
 * aussi le `max` du contrat partagé (`CanvasInspectBlockSchema.text`) : la
 * changer ici sans changer là-bas fait rejeter le receipt ENTIER.
 */
const MAX_MODEL_TEXT_LENGTH = 2_000;

export interface CanvasSnapshotBlock {
  nodeId: string;
  ordinal: number;
  nodeType: string;
  /**
   * Texte complet du bloc, NON borné — surface du résolveur de sélecteurs.
   *
   * Il reste retiré du receipt (`toModelSafeCanvasBlocks`) : `text` en porte
   * désormais le contenu à la borne près, donc le publier deux fois doublerait
   * le coût de la fenêtre sans rien apprendre au modèle.
   */
  fullText: string;
  /** Texte du bloc rendu au modèle, borné à `MAX_MODEL_TEXT_LENGTH` (#1008). */
  text: string;
  /**
   * Posé UNIQUEMENT quand `text` est plus court que `fullText`.
   *
   * Son absence dit « ce bloc est rendu en entier » ; sa présence dit « la fin
   * du paragraphe manque, un sélecteur textuel peut viser du texte que tu ne
   * vois pas ». Sans ce champ, les deux cas sont indiscernables au modèle.
  */
  textTruncated?: true;
  headingLevel?: number;
  listLevel?: number;
  /**
   * ⚠️ **Ce champ promet plus que le runtime ne publie** (relevé du 2026-08-16,
   * `tests/ws-mocked/mesure-lecture-par-bloc.spec.ts`).
   *
   * Il est alimenté depuis l'entrée `blocks.list`, et en `superdoc@2.6.0` cette
   * surface ne remplit plus que `styleId` : `fontFamily`, `fontSize` et
   * `alignment` sont déclarés par `BlockListEntry`, PEINTS à l'écran, et jamais
   * publiés. `canvas_inspect` les rend donc systématiquement absents à l'agent —
   * et **un signal absent n'est pas un signal négatif** : « pas de police
   * rapportée » ne veut pas dire « police par défaut ».
   *
   * Les trois champs sont CONSERVÉS, pas retirés : ils redeviennent vrais si le
   * fournisseur recommence à les publier, et deux sondes de référence les
   * consomment (`canvas-generation-reference-live.spec.ts` juge la taille de
   * titre dessus). Ne rien conclure de leur absence en attendant.
   *
   * Le pinceau, lui, ne dépend PLUS de cette lecture : il lit aussi
   * `doc.getNodeById` (voir `effectiveStyle.ts`). Faire de même ici coûterait un
   * appel par bloc listé, sur une fenêtre d'inspection qui va jusqu'à 80 blocs —
   * c'est un changement de complexité, pas une annotation, et il n'est pas fait
   * dans #991.
   */
  effectiveStyle?: { styleId?: string; fontFamily?: string; fontSize?: number; alignment?: string };
  /**
   * Saut de page AVANT ce bloc — lu depuis `SDParagraphProps.pageBreakBefore` du corps SDM/1
   * (`doc.get()`), jamais du CSS ni du DOM. Instrument de lecture additif posé pour la sonde
   * de référence #929 (spec de migration SuperDoc V2 #928) : c'est la propriété que
   * l'export OOXML sérialise en `w:pageBreakBefore` — voir `features/redaction/CLAUDE.md`
   * "Sauts de page".
   */
  pageBreakBefore?: boolean;
  /**
   * Présence/largeur d'une table — lu depuis `SDTable.columns[].width` (largeur de grille en
   * points, équivalent public de `w:gridCol`, converti en twips) et `SDTableProps.width`
   * (union `SDTableWidth`, variante `percent`). Même instrument de lecture additif que
   * `pageBreakBefore` ci-dessus ; voir `features/redaction/CLAUDE.md` "Largeur des colonnes
   * de table".
   */
  table?: { columnWidthsTwips: number[]; widthPercent: number | null };
}

/** Snapshot block after resolver-only text has been removed from the model receipt. */
export type ModelSafeCanvasSnapshotBlock = Omit<CanvasSnapshotBlock, 'fullText'>;

/** Bornes des textes d'une révision publiés au modèle — un acte entier peut être supprimé d'un geste. */
const MAX_REVISION_TEXT_LENGTH = 300;

/**
 * Une révision suivie, telle que `trackChanges.list()` la rend — RELEVÉE, pas
 * devinée (#994, `tests/ws-mocked/mesure-994-formes-de-revision.spec.ts`).
 *
 * ## Pourquoi ce type existe
 *
 * `accept_tracked_changes` et `reject_tracked_changes` exigent des identifiants
 * de révision, et **aucune surface ne les fournissait au modèle** : ils étaient
 * inatteignables depuis leur mise en service. Pendant ce temps trois lecteurs
 * différents appelaient `trackChanges.list()` pour n'en garder qu'une longueur
 * ou qu'un ensemble d'identifiants, et jetaient tout le reste. Ce type est le
 * lecteur unique, et il garde ce qui permet de DÉCIDER.
 *
 * ## Les champs, et ce que chacun vaut vraiment
 *
 * Mesuré le 2026-08-16 sur `superdoc@2.6.0`, sur quatre gestes :
 *
 * | geste | `type` / `subtype` | `excerpt` | `insertedText` | `deletedText` |
 * |---|---|---|---|---|
 * | `create.paragraph` | `structural` / `paragraph-insertion` | `"paragraph insertion"` | le texte inséré | `null` |
 * | `doc.replace` | `replacement` / `text-replacement` | le texte inséré | le texte inséré | le texte remplacé |
 * | `blocks.delete` | `deletion` / `text-deletion` | le texte supprimé | `null` | le texte supprimé |
 *
 * La montée à `superdoc@2.15.1` a fait évoluer la forme observable de
 * `create.paragraph` dans Chromium : `insertion` / `text-insertion`, toujours
 * ancrée par `navigationTarget.blockId`. Le ledger de retrait d'une proposition
 * agent accepte les deux formes mesurées, mais seulement après avoir établi sa
 * provenance causale exacte ; aucun appelant ne doit déduire une propriété
 * d'auteur de cette taxonomie.
 *
 * ⚠️ **`excerpt` n'est PAS toujours un extrait du document** : sur une insertion
 * de paragraphe il vaut la chaîne `"paragraph insertion"`, c'est-à-dire un
 * LIBELLÉ de nature. Le publier seul montrerait au modèle une étiquette là où il
 * attend du texte, et il conclurait de travers sur ce que la révision contient.
 * C'est pour cela que `insertedText` et `deletedText` sont publiés À CÔTÉ, et
 * jamais fondus dans une phrase construite ici : au modèle de lire les trois.
 *
 * `type` et `subtype` sont laissés en chaîne libre, pas en enum : l'énumération
 * du fournisseur n'est pas fermée (`structural`, `replacement`, `deletion` ont
 * été vus ; d'autres existent — mise en forme, propriétés de paragraphe), et un
 * enum trop étroit ferait rejeter le receipt ENTIER sur une valeur inconnue.
 *
 * `blockId` vient de `navigationTarget.blockId`, « the authoritative block
 * address for review navigation » : c'est lui qui rattache une révision à un
 * bloc que le modèle a déjà lu. Optionnel au contrat, donc son absence ne dit
 * jamais « cette révision n'est nulle part ».
 */
export interface CanvasSnapshotRevision {
  id: string;
  type?: string;
  subtype?: string;
  /** `open` sur toutes les révisions mesurées. Une révision décidée quitte la liste. */
  state?: string;
  author?: string;
  /** ISO 8601, tel que le fournisseur le rend. */
  date?: string;
  excerpt?: string;
  insertedText?: string;
  deletedText?: string;
  blockId?: string;
}

/** Provider read failures are reduced to a stable code before crossing canvas boundaries. */
export class CanvasSnapshotReadError extends Error {
  constructor(readonly code: string) {
    super('SuperDoc blocks.list returned success:false.');
  }
}

export function isCanvasSnapshotReadError(error: unknown): error is CanvasSnapshotReadError {
  return error instanceof CanvasSnapshotReadError;
}

function texteBorneA(value: unknown, max: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  // L'ellipse compte dans la borne : le contrat partagé refuse `max + 1`
  // caractères, et l'inspection entière avec (`CANVAS_INSPECT_RECEIPT_INVALID`).
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}

function texteBorne(value: unknown): string | undefined {
  return texteBorneA(value, MAX_REVISION_TEXT_LENGTH);
}

/**
 * Un texte du document, borné comme {@link CanvasSnapshotRevision.insertedText} : ce qu'une révision qui porterait
 * exactement ce texte publie. Seule façon de comparer un texte lu ailleurs à celui d'une révision, qui sort d'ici rogné.
 */
export function texteCommeUneRevisionLePublie(texte: string): string | undefined {
  return texteBorne(texte);
}

/** Vrai quand un texte de révision a été coupé à la borne : ce qu'il porte au-delà n'est pas lisible ici. */
export function estUnTexteDeRevisionCoupe(texte: string | undefined): boolean {
  return texte !== undefined && texte.length === MAX_REVISION_TEXT_LENGTH && texte.endsWith('…');
}

/**
 * LE lecteur des révisions suivies — il n'y en a qu'un (#994).
 *
 * ⚠️ **La clé est `items`, pas `changes`.** `trackChanges.list()` rend
 * `{evaluatedRevision, total, items, page}` (mesuré 2026-08-16). Une sonde qui
 * avait deviné `changes` a rapporté « 0 révision partout, témoin compris » : un
 * relevé parfaitement cohérent et entièrement faux, parce qu'une clé inexistante
 * rend un vide **indiscernable** d'un vrai zéro. Le tableau nu est aussi accepté
 * — c'est la forme des doubles de test historiques.
 *
 * Une entrée sans `id` exploitable est ÉCARTÉE : une révision qu'on ne peut pas
 * nommer ne peut pas être décidée, et la publier ferait construire au modèle un
 * appel qui échouerait à coup sûr.
 */
export function lireLesRevisionsSuivies(source: unknown): CanvasSnapshotRevision[] {
  return items(source).flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const id = stringValue(entry.id);
    if (id === undefined) return [];
    const navigation = isRecord(entry.navigationTarget) ? entry.navigationTarget : undefined;
    return [{
      id,
      ...(stringValue(entry.type) === undefined ? {} : { type: stringValue(entry.type) }),
      ...(stringValue(entry.subtype) === undefined ? {} : { subtype: stringValue(entry.subtype) }),
      ...(stringValue(entry.state) === undefined ? {} : { state: stringValue(entry.state) }),
      ...(stringValue(entry.author) === undefined ? {} : { author: stringValue(entry.author) }),
      ...(stringValue(entry.date) === undefined ? {} : { date: stringValue(entry.date) }),
      ...(texteBorne(entry.excerpt) === undefined ? {} : { excerpt: texteBorne(entry.excerpt) }),
      ...(texteBorne(entry.insertedText) === undefined ? {} : { insertedText: texteBorne(entry.insertedText) }),
      ...(texteBorne(entry.deletedText) === undefined ? {} : { deletedText: texteBorne(entry.deletedText) }),
      ...(stringValue(navigation?.blockId) === undefined ? {} : { blockId: stringValue(navigation?.blockId) }),
    }];
  });
}

/**
 * Result of the security-critical, exhaustive tracked-change reader.
 *
 * `readCanvasSnapshot()` deliberately keeps its model-facing read bounded: an
 * inspection must remain useful even when an unrelated review surface is
 * degraded. A destructive operation cannot make that trade-off. It must prove
 * whether a paragraph belongs to an open wrapper before calling `blocks.delete`
 * or clearing causal provenance after a decision.
 */
export type CompleteTrackedChangesRead =
  | { ok: true; trackedChanges: CanvasSnapshotRevision[] }
  | { ok: false; reason: string };

/**
 * An exhaustive discovery can be used for two different purposes: a
 * destructive review decision needs the full provider limit, while an optional
 * provenance ledger may impose a tighter operational budget. A caller that
 * receives `ok:false` must not infer an empty review state.
 */
export interface CompleteTrackedChangesReadOptions {
  readonly maxTrackedChanges?: number;
}

/** A revision fence is deliberately narrower than a tracked-change discovery. */
type DocumentRevisionRead =
  | { ok: true; revision: RevisionDeDocument }
  | { ok: false; reason: string };

const COMPLETE_TRACKED_CHANGES_PAGE_SIZE = 200;
const MAX_COMPLETE_TRACKED_CHANGES = 10_000;

/**
 * Reads every open tracked change across every story, or says that this cannot
 * be proven. The `{ in: 'all' }` scope is load-bearing: the default provider
 * query covers only the body, while an insertion wrapper can be in another
 * revision-capable story.
 *
 * Unlike the model-facing parser, this reader rejects an array-only legacy
 * response, malformed item, duplicate identity, incomplete page, unstable
 * total, or unstable evaluated revision. Each of those states is ambiguous for
 * an automatic review decision and must stay fail-closed.
 */
export async function readCompleteTrackedChanges(
  doc: SuperDocDocumentApi,
  options: CompleteTrackedChangesReadOptions = {},
): Promise<CompleteTrackedChangesRead> {
  if (typeof doc.trackChanges?.list !== 'function') {
    return { ok: false, reason: 'editor.doc.trackChanges.list is unavailable' };
  }
  const maximum = options.maxTrackedChanges ?? MAX_COMPLETE_TRACKED_CHANGES;
  if (!Number.isSafeInteger(maximum) || maximum < 0 || maximum > MAX_COMPLETE_TRACKED_CHANGES) {
    return { ok: false, reason: `tracked-change discovery requires maxTrackedChanges between 0 and ${MAX_COMPLETE_TRACKED_CHANGES}` };
  }

  const trackedChanges: CanvasSnapshotRevision[] = [];
  const ids = new Set<string>();
  let total: number | undefined;
  let evaluatedRevision: string | undefined;

  while (total === undefined || trackedChanges.length < total) {
    let page: unknown;
    try {
      page = await doc.trackChanges.list({
        in: 'all',
        offset: trackedChanges.length,
        limit: COMPLETE_TRACKED_CHANGES_PAGE_SIZE,
      });
    } catch (error) {
      const detail = error instanceof Error ? error.message.slice(0, 160) : String(error).slice(0, 160);
      return { ok: false, reason: `trackChanges.list(all) threw: ${detail}` };
    }

    const envelope = isRecord(page) ? page : undefined;
    const pagination = envelope !== undefined && isRecord(envelope.page) ? envelope.page : undefined;
    const pageTotal = safeIntegerValue(envelope?.total);
    const pageOffset = safeIntegerValue(pagination?.offset);
    const pageLimit = safeIntegerValue(pagination?.limit);
    const pageReturned = safeIntegerValue(pagination?.returned);
    const pageRevision = stringValue(envelope?.evaluatedRevision);
    if (envelope === undefined || (envelope.success !== undefined && envelope.success !== true) || !Array.isArray(envelope.items)
      || pageTotal === undefined || pageTotal < 0 || pageTotal > MAX_COMPLETE_TRACKED_CHANGES
      || pageRevision === undefined
      || pagination === undefined
      || pageOffset === undefined || pageOffset !== trackedChanges.length
      || pageLimit === undefined || pageLimit <= 0
      || pageReturned === undefined || pageReturned !== envelope.items.length
      || envelope.items.length > pageLimit) {
      return { ok: false, reason: 'trackChanges.list(all) did not return a complete paginated discovery envelope' };
    }
    if (pageTotal > maximum) {
      return {
        ok: false,
        reason: `trackChanges.list(all) reports ${pageTotal} revision(s), above this discovery budget of ${maximum}`,
      };
    }

    if ((total !== undefined && total !== pageTotal) || (evaluatedRevision !== undefined && evaluatedRevision !== pageRevision)) {
      return { ok: false, reason: 'trackChanges.list(all) changed total or evaluatedRevision during pagination' };
    }
    total = pageTotal;
    evaluatedRevision = pageRevision;

    if ((envelope.items.length === 0 && trackedChanges.length < total)
      || trackedChanges.length + envelope.items.length > total) {
      return { ok: false, reason: `trackChanges.list(all) stopped at ${trackedChanges.length}/${total} revision(s)` };
    }

    const normalized = lireLesRevisionsSuivies({ items: envelope.items });
    if (normalized.length !== envelope.items.length) {
      return { ok: false, reason: 'trackChanges.list(all) returned a malformed or duplicate tracked-change identity' };
    }
    // Insert one identity at a time: checking only the prior pages would miss
    // two equal ids in the current page and falsely certify an ambiguous list.
    for (const revision of normalized) {
      if (ids.has(revision.id)) {
        return { ok: false, reason: 'trackChanges.list(all) returned a malformed or duplicate tracked-change identity' };
      }
      ids.add(revision.id);
      trackedChanges.push(revision);
    }
  }

  return { ok: true, trackedChanges };
}

/**
 * Reads exhaustive review state only while the optimistic document revision is
 * provably stable. `evaluatedRevision` is a distinct provider read-model
 * namespace, so it cannot replace either `doc.info()` fence here.
 */
export async function readCompleteTrackedChangesAtDocumentRevision(
  doc: SuperDocDocumentApi,
  expectedRevision: RevisionDeDocument,
  options: CompleteTrackedChangesReadOptions = {},
): Promise<CompleteTrackedChangesRead> {
  if (expectedRevision === REVISION_ABSENTE) {
    return { ok: false, reason: 'editor.doc.info() did not expose the document revision needed to bind the exhaustive tracked-change read' };
  }
  const revisionBefore = await revisionForCompleteTrackedChangesRead(doc, 'before');
  if (!revisionBefore.ok) return revisionBefore;
  if (revisionBefore.revision !== expectedRevision) {
    return { ok: false, reason: 'the document revision changed before the exhaustive tracked-change read began' };
  }
  const complete = await readCompleteTrackedChanges(doc, options);
  if (!complete.ok) return complete;
  const revisionAfter = await revisionForCompleteTrackedChangesRead(doc, 'after');
  if (!revisionAfter.ok) return revisionAfter;
  if (revisionAfter.revision !== expectedRevision) {
    return { ok: false, reason: 'the document revision changed while exhaustive tracked changes were being read' };
  }
  return complete;
}

async function revisionForCompleteTrackedChangesRead(
  doc: SuperDocDocumentApi,
  boundary: 'before' | 'after',
): Promise<DocumentRevisionRead> {
  try {
    const revision = await lireLaRevisionDuDocument(doc);
    return revision === undefined
      ? { ok: false, reason: `editor.doc.info() did not expose the document revision ${boundary} the exhaustive tracked-change read` }
      : { ok: true, revision };
  } catch (error) {
    const detail = error instanceof Error ? error.message.slice(0, 160) : String(error).slice(0, 160);
    return { ok: false, reason: `editor.doc.info() threw ${boundary} the exhaustive tracked-change read: ${detail}` };
  }
}

/** Les seuls identifiants — pour les surfaces qui comparent des ensembles avant/après. */
export function idsDeRevision(revisions: readonly CanvasSnapshotRevision[]): string[] {
  return revisions.map((revision) => revision.id);
}

/** Bornes des textes de commentaire publiés au modèle — miroir du schéma partagé. */
const MAX_COMMENT_TEXT_LENGTH = 1_000;
const MAX_COMMENT_ANCHOR_LENGTH = 400;

/**
 * Un commentaire, tel que `comments.list()` le rend — RELEVÉ, pas deviné (#996,
 * `test-results/tranche3-996.json`, mesure 13).
 *
 * ## Pourquoi ce type existe
 *
 * Même cause exactement qu'en #994 pour les révisions : `resolve_comments` et
 * `reply_to_comment` exigent un `commentId`, et l'inspection ne publiait qu'un
 * `counts.comments` — c'est-à-dire un NOMBRE. Les deux verbes étaient donc
 * **inappelables**, sauf à inventer un identifiant.
 *
 * ## Forme mesurée d'une entrée
 *
 * `{id, handle:{ref:'comment:0', refStability:'stable', targetKind:'comment'},
 * address:{kind:'entity', entityType:'comment', entityId:'0'}, commentId:'0',
 * text, isInternal:false, status:'open', creatorName:'Avocat', rawOoxmlId:'0',
 * replyCount:0, target:{kind:'text', segments:[{blockId, range}]}, anchoredText}`
 *
 * ⚠️ **`status` est ASYMÉTRIQUE entre lecture et écriture** : on relit `'open'`,
 * et réécrire `'open'` est REFUSÉ (« status must be "resolved" or "active" »).
 * Le schéma partagé ferme donc la lecture à `open | resolved`, et la traduction
 * vers le vocabulaire d'écriture vit dans l'exécuteur. Toute autre valeur
 * provider est **omise** plutôt que publiée : le contrat est `.strict()`, et une
 * valeur inconnue ferait rejeter le receipt ENTIER — c'est-à-dire perdre la
 * lecture du document pour cause de vocabulaire.
 *
 * ⚠️ `creatorName` (pas `author`) est le nom mesuré côté provider ; le contrat
 * exposé au modèle le publie sous `author`.
 */
export interface CanvasSnapshotComment {
  commentId: string;
  text?: string;
  status?: 'open' | 'resolved';
  author?: string;
  replyCount?: number;
  /** L'extrait du document que ce commentaire vise. */
  anchoredText?: string;
  /** Le bloc porteur, quand la cible tient dans un SEUL segment. */
  blockId?: string;
  /** Présent sur une RÉPONSE, absent sur la racine d'un fil. */
  parentCommentId?: string;
}

/** `open` / `resolved` seulement — toute autre valeur provider est OMISE. */
function statutLisible(value: unknown): 'open' | 'resolved' | undefined {
  return value === 'open' || value === 'resolved' ? value : undefined;
}

/**
 * Le bloc porteur, quand la cible ne couvre QU'UN segment.
 *
 * Sur une ancre à cheval sur plusieurs blocs, publier le premier ferait croire
 * au modèle que le commentaire ne porte que sur celui-là — mieux vaut omettre.
 */
function blockIdDeLaCible(target: unknown): string | undefined {
  if (!isRecord(target)) return undefined;
  const segments = Array.isArray(target.segments) ? target.segments : [];
  if (segments.length !== 1) return undefined;
  const premier = segments[0];
  return isRecord(premier) ? stringValue(premier.blockId) : undefined;
}

/**
 * LE lecteur des commentaires — il n'y en a qu'un (#996).
 *
 * ⚠️ La clé est `items` (forme `{evaluatedRevision, total, items, page}`), comme
 * pour `trackChanges.list`. Le tableau nu est aussi accepté : c'est la forme des
 * doubles de test historiques.
 *
 * Une entrée sans `commentId` exploitable est ÉCARTÉE : un commentaire qu'on ne
 * peut pas nommer ne peut être ni résolu ni répondu, et le publier ferait
 * construire au modèle un appel qui échouerait à coup sûr.
 */
export function lireLesCommentaires(source: unknown): CanvasSnapshotComment[] {
  return items(source).flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const commentId = stringValue(entry.commentId) ?? stringValue(entry.id);
    if (commentId === undefined) return [];
    // ⚠️ `comments.list` MÊLE des révisions aux commentaires. Observé le
    // 2026-08-16 : après un `doc.insert` suivi, la liste gagne une entrée
    //   `tc-comment:tc|main%3A%2Fword%2Fdocument.xml|ins|0000000D%7CwId%3A6`
    // là où les vrais commentaires portent `0`, `1`, `2`…
    //
    // Publier ces entrées donnerait au modèle des « commentaires » qui sont des
    // modifications suivies : il ne peut ni les résoudre (elles n'ont pas de
    // fil) ni y répondre, et `counts.comments` mentirait sur un document en
    // cours de relecture — c'est-à-dire dans le cas d'usage même de ces verbes.
    //
    // Filtre DÉFENSIF assumé : le phénomène a été vu une fois, avec un
    // `doc.insert` de plage vide, et n'a PAS pu être reproduit avec
    // `doc.replace`. La condition exacte reste donc inconnue, et ce préfixe est
    // le seul discriminant observé — aucun champ propre ne distingue les deux
    // sortes. Écarter ces entrées ne coûte rien (les révisions ont déjà leur
    // domaine, `trackedChanges`) et ferme une pollution réelle.
    if (commentId.startsWith('tc-comment:')) return [];
    const statut = statutLisible(entry.status);
    const auteur = stringValue(entry.creatorName) ?? stringValue(entry.author);
    const replyCount = numberValue(entry.replyCount);
    const parent = stringValue(entry.parentCommentId) ?? stringValue(entry.parentId);
    const ancre = texteBorneA(entry.anchoredText, MAX_COMMENT_ANCHOR_LENGTH);
    const texte = texteBorneA(entry.text, MAX_COMMENT_TEXT_LENGTH);
    const blockId = blockIdDeLaCible(entry.target);
    return [{
      commentId,
      ...(texte === undefined ? {} : { text: texte }),
      ...(statut === undefined ? {} : { status: statut }),
      ...(auteur === undefined ? {} : { author: auteur }),
      ...(replyCount === undefined || replyCount < 0 ? {} : { replyCount }),
      ...(ancre === undefined ? {} : { anchoredText: ancre }),
      ...(blockId === undefined ? {} : { blockId }),
      ...(parent === undefined ? {} : { parentCommentId: parent }),
    }];
  });
}

/** Borne du texte de note publié au modèle : le max de `CanvasFootnoteSchema.text`. */
const MAX_FOOTNOTE_TEXT_LENGTH = 1_000;

/**
 * Une note de bas de page, telle que `footnotes.list()` la rend — RELEVÉE, pas
 * devinée (relevé du 2026-08-26, §9.3 de
 * `docs/features/redaction-web-canvas/superdoc-document-api.md`).
 *
 * ## Pourquoi ce type existe
 *
 * Même cause exactement qu'en #994 pour les révisions et #996 pour les
 * commentaires, une troisième fois : `edit_footnote` et `delete_footnote`
 * exigent tous deux un `note_id`, et aucune surface ne le publiait. Les deux
 * verbes seraient donc **inappelables** dès leur mise en service, sauf à
 * inventer un identifiant. Un identifiant n'atteint le modèle QUE par
 * `canvas_inspect`.
 *
 * ## Forme mesurée d'une entrée
 *
 * `{id, handle:{ref, refStability, targetKind}, address:{kind:'entity',
 * entityType:'footnote', noteId}, type, noteId, displayNumber, content}`
 *
 * ⚠️ **Le texte de la note s'appelle `content` chez le fournisseur, et `text`
 * au contrat** — c'est la seule renomination de cette lecture, et elle est
 * délibérée : le modèle lit déjà `text` sur un bloc, sur un commentaire et sur
 * une révision. Lui apprendre un quatrième mot pour la même chose (« le texte
 * de cet objet ») coûterait un vocabulaire de plus sans rien lui apprendre.
 * Deviner l'inverse — lire `text` chez le fournisseur — rendrait un vide
 * **indiscernable d'une note sans contenu**, exactement le piège de la clé
 * `changes` en #994.
 *
 * ⚠️ **`type` distingue note de bas de page et note de fin.** Le fournisseur
 * n'a pas d'opération `endnote` séparée : le type voyage en paramètre (§9.1).
 * Toute valeur hors `footnote | endnote` est OMISE plutôt que publiée — le
 * contrat est `.strict()` et une valeur inconnue ferait rejeter le receipt
 * ENTIER, c'est-à-dire perdre la lecture du document pour cause de vocabulaire.
 */
export interface CanvasSnapshotFootnote {
  noteId: string;
  /** Le renvoi tel qu'il est PEINT (`1`, `i`, `*`), pas un rang. */
  displayNumber?: string;
  /** `content` côté fournisseur — voir la renomination ci-dessus. */
  text?: string;
  type?: 'footnote' | 'endnote';
}

/** `footnote` / `endnote` seulement — toute autre valeur provider est OMISE. */
function typeDeNoteLisible(value: unknown): 'footnote' | 'endnote' | undefined {
  return value === 'footnote' || value === 'endnote' ? value : undefined;
}

/**
 * Le renvoi, ramené à la CHAÎNE que le contrat exige.
 *
 * Le contrat ferme `displayNumber` à une chaîne parce que le renvoi est déjà
 * formaté par le vocabulaire de numérotation du document (`1`, `i`, `*`). Un
 * fournisseur qui rendrait le rang en nombre est toléré ici : sa forme décimale
 * EST le renvoi tel qu'il est peint en numérotation arabe, et l'écarter
 * priverait le modèle du seul repère lisible par l'avocat — « la note 3 » est
 * ce que celui-ci dira, jamais l'identifiant opaque.
 */
function numeroAffiche(value: unknown): string | undefined {
  const chaine = stringValue(value);
  if (chaine !== undefined) return chaine;
  const nombre = numberValue(value);
  return nombre === undefined ? undefined : String(nombre);
}

/**
 * LE lecteur des notes de bas de page — il n'y en a qu'un.
 *
 * ⚠️ La clé est `items` (forme `{evaluatedRevision, total, items, page}`, §9.3),
 * la MÊME enveloppe de découverte que `trackChanges.list` et `comments.list`.
 * Le tableau nu est aussi accepté : c'est la forme des doubles de test.
 *
 * Une entrée sans `noteId` exploitable est ÉCARTÉE — même raisonnement qu'une
 * entrée sans `commentId` en #996 : une note qu'on ne peut pas nommer ne peut
 * être ni modifiée ni supprimée, et la publier ferait construire au modèle un
 * appel qui échouerait à coup sûr.
 *
 * ⚠️ **Ce que cette liste dit, et ce qu'elle ne dit pas** : sous régime suivi,
 * une note supprimée y RESTE, avec son contenu, jusqu'à ce que l'avocat accepte
 * la révision (mesuré le 2026-08-26, §9.4). Elle décrit ce que le document
 * PORTE, jamais ce que l'avocat VERRA — compter les notes après une suppression
 * ne prouve donc rien.
 */
export function lireLesNotes(source: unknown): CanvasSnapshotFootnote[] {
  return items(source).flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const noteId = stringValue(entry.noteId) ?? stringValue(entry.id);
    if (noteId === undefined) return [];
    const displayNumber = numeroAffiche(entry.displayNumber);
    // `content` d'abord, `text` en repli : la forme mesurée porte `content`, et
    // le repli couvre les doubles de test et un fournisseur qui s'aligne un jour
    // sur le nom des autres domaines.
    const texte = texteBorneA(entry.content, MAX_FOOTNOTE_TEXT_LENGTH)
      ?? texteBorneA(entry.text, MAX_FOOTNOTE_TEXT_LENGTH);
    const type = typeDeNoteLisible(entry.type);
    return [{
      noteId,
      ...(displayNumber === undefined ? {} : { displayNumber }),
      ...(texte === undefined ? {} : { text: texte }),
      ...(type === undefined ? {} : { type }),
    }];
  });
}

/**
 * Borne du texte publié PAR PARAGRAPHE d'en-tête, alignée sur
 * `CanvasHeaderFooterZoneSchema.paragraphs[].text` (`.max(500)`).
 *
 * Un en-tête est une ligne de cabinet ou une référence de dossier, pas de la
 * prose : 500 caractères en couvrent largement le pire cas réel, et le contrat
 * étant `.strict()`, dépasser ferait rejeter l'inspection ENTIÈRE — blocs et
 * jeton de révision compris — pour un en-tête bavard.
 */
const MAX_HEADER_PARAGRAPH_LENGTH = 500;

/** Borne du texte concaténé d'une zone, alignée sur `CanvasHeaderFooterZoneSchema.text`. */
const MAX_HEADER_ZONE_TEXT_LENGTH = 1_000;

/** Borne du nombre de paragraphes publiés par zone, alignée sur le contrat (`.max(30)`). */
const MAX_HEADER_PARAGRAPHS = 30;

/**
 * Les trois variantes SuperDoc, indexées par le nom de zone du contrat Hector.
 *
 * ⚠️ **C'est LA table de traduction, et il n'en existe qu'une.** Le contrat
 * agent parle `primary` / `first_page` / `even_pages` — le vocabulaire de
 * l'add-in Word — pendant que le fournisseur parle `default` / `first` /
 * `even`. Recopier cette correspondance dans l'exécuteur d'écriture ferait
 * exister deux tables qui divergeraient au premier ajout de variante.
 */
export const VARIANTE_SUPERDOC_PAR_ZONE: Readonly<Record<CanvasHeaderFooterType, 'default' | 'first' | 'even'>> = {
  primary: 'default',
  first_page: 'first',
  even_pages: 'even',
};

/**
 * Une FAMILLE d'opérations du Document API (`doc.sections`, `doc.footnotes`…),
 * ou `undefined` si le document ne la porte pas.
 *
 * ⚠️ **Une famille peut être un objet OU une fonction, et rien ne dit laquelle**
 * (mesuré le 2026-09-05 sur `superdoc@2.6.0`) : `footnotes`, `comments`,
 * `blocks`, `trackChanges`, `query`, `history` et `hyperlinks` sont des objets ;
 * `create`, `format`, `styles`, `lists`, `tables`, **`sections`,
 * `headerFooters` et `images`** sont des **proxys APPELABLES**, dont `typeof`
 * rend `'function'` et dont `Object.keys` ne rend que `__family` / `__path`.
 *
 * Un `typeof famille === 'object'` — c'est-à-dire un `isRecord` — rejette donc
 * la moitié du fournisseur, **en silence** : la lecture rend le vide, et un vide
 * de ce genre est indiscernable d'un document qui n'a rien. C'est exactement ce
 * qui a fait rendre « aucune section » sur un document qui en publie une
 * (2026-09-05, sonde `canvas-entetes-et-pieds`). La répartition n'a aucune règle
 * devinable et peut changer d'une version à l'autre : **toute lecture de famille
 * passe par ici**.
 */
export function familleDuDocument(doc: unknown, nom: string): Record<string, unknown> | undefined {
  if (doc === null || (typeof doc !== 'object' && typeof doc !== 'function')) return undefined;
  const famille = (doc as Record<string, unknown>)[nom];
  if (famille === null || (typeof famille !== 'object' && typeof famille !== 'function')) return undefined;
  return famille as Record<string, unknown>;
}

/**
 * Une opération d'une famille, liée à son objet porteur.
 *
 * ⚠️ Le `.bind` n'est pas décoratif : détacher la méthode de sa famille lui
 * ferait perdre son `this`, et le fournisseur en a un.
 *
 * Une opération se lit par ici, ou s'appelle sur sa famille
 * (`famille.operation(...)`) ; jamais `const f = doc.famille.operation`
 * suivi de `f(...)`. Une sous-famille se compose :
 * `operationDuDocument(familleDuDocument(doc, 'format'), 'paragraph', 'setAlignment')`.
 * Un appel détaché ne casse rien de visible : le `catch` qui entoure presque
 * toute écriture fait de l'exception un refus muet (revue de la PR #2257).
 *
 * Le test d'un appelant prend pour double une CLASSE dont la méthode lit
 * `this` : une fonction fléchée ou un `vi.fn` passe sans receveur et ne voit
 * pas l'extraction.
 */
export function operationDuDocument(
  doc: unknown,
  famille: string,
  operation: string,
): SuperDocDocumentMethod | undefined {
  const porteur = familleDuDocument(doc, famille);
  const methode = porteur?.[operation];
  return typeof methode === 'function' ? (methode as SuperDocDocumentMethod).bind(porteur) : undefined;
}

/** L'adresse de story qu'accepte `create.paragraph`, `blocks.list` et `images.list`. */
export function storyDuSlot(
  section: { kind: 'section'; sectionId: string },
  cible: 'header' | 'footer',
  zone: CanvasHeaderFooterType,
): {
  kind: 'story';
  storyType: 'headerFooterSlot';
  section: { kind: 'section'; sectionId: string };
  headerFooterKind: 'header' | 'footer';
  variant: 'default' | 'first' | 'even';
} {
  return {
    kind: 'story',
    storyType: 'headerFooterSlot',
    section,
    headerFooterKind: cible,
    variant: VARIANTE_SUPERDOC_PAR_ZONE[zone],
  };
}

/** L'adresse de slot qu'accepte `headerFooters.resolve`. */
export function slotDEnTete(
  section: { kind: 'section'; sectionId: string },
  cible: 'header' | 'footer',
  zone: CanvasHeaderFooterType,
): {
  kind: 'headerFooterSlot';
  section: { kind: 'section'; sectionId: string };
  headerFooterKind: 'header' | 'footer';
  variant: 'default' | 'first' | 'even';
} {
  return {
    kind: 'headerFooterSlot',
    section,
    headerFooterKind: cible,
    variant: VARIANTE_SUPERDOC_PAR_ZONE[zone],
  };
}

/** Le statut de résolution d'un slot — `none` sur toute réponse illisible. */
export function lireLeStatutDeSlot(valeur: unknown): 'explicit' | 'inherited' | 'none' {
  if (!isRecord(valeur)) return 'none';
  const { status } = valeur;
  return status === 'explicit' || status === 'inherited' ? status : 'none';
}

/** L'adresse de section publiée par `sections.list()`, ou `null` si illisible. */
function adresseDeSection(entree: unknown): { kind: 'section'; sectionId: string } | null {
  if (!isRecord(entree)) return null;
  const adresse = isRecord(entree.address) ? entree.address : entree;
  const sectionId = stringValue(adresse.sectionId);
  return sectionId === undefined ? null : { kind: 'section', sectionId };
}

/**
 * Un booléen de section — `null` quand le fournisseur ne publie PAS la clé.
 *
 * ⚠️ Mesuré le 2026-09-05 : `sections.list()` publie `oddEvenHeadersFooters:
 * false` mais OMET `titlePage` tant qu'il n'a jamais été levé. Replier
 * l'absence sur `false` ferait donc affirmer « ce document n'a pas de première
 * page distincte » sur la foi d'une clé que le fournisseur peut cesser de
 * publier pour une autre raison. Le contrat expose un `boolean | null` pour
 * cette raison précise, et `set_header_footer` lève le drapeau lui-même plutôt
 * que de se fier à cette lecture.
 */
function booleenDeSection(valeur: unknown): boolean | null {
  return typeof valeur === 'boolean' ? valeur : null;
}

/** L'alignement d'un paragraphe d'en-tête, restreint au vocabulaire du contrat. */
function alignementLisible(valeur: unknown): 'left' | 'center' | 'right' | 'justify' | undefined {
  return valeur === 'left' || valeur === 'center' || valeur === 'right' || valeur === 'justify'
    ? valeur
    : undefined;
}

/** Les `blockId` d'une zone qui portent une image, d'après `images.list`. */
function blocsPorteursDImage(valeur: unknown): Set<string> {
  const porteurs = new Set<string>();
  for (const entree of items(valeur)) {
    if (!isRecord(entree)) continue;
    const ancre = isRecord(entree.anchorRef) ? entree.anchorRef : undefined;
    const blockId = stringValue(ancre?.blockId);
    if (blockId !== undefined) porteurs.add(blockId);
  }
  return porteurs;
}

/**
 * UNE zone d'en-tête ou de pied de page, lue de bout en bout.
 *
 * ⚠️ **`has_image` n'est PAS déductible de `blocks.list`, et c'est le piège le
 * plus coûteux de cette lecture** (mesuré le 2026-09-05,
 * `tests/ws-mocked/mesure-en-tetes-et-pieds.spec.ts`) : un paragraphe qui ne
 * porte QU'UNE image se relit `text:''`, `isEmpty:true` — donc exactement comme
 * une ligne blanche. Or c'est là que vit le papier à en-tête du cabinet. Sans
 * `images.list`, l'agent lirait « en-tête vide » sur un logo, et un
 * `mode:'replace'` naïf le supprimerait.
 *
 * `images.list` est best-effort : sur un runtime qui ne la publie pas, on rend
 * `has_image:false` — indiscernable d'une vraie absence, limite assumée et
 * DITE dans `INSPECT_DESCRIPTION`, parce que l'alternative (renoncer à publier
 * le domaine) coûterait la lecture entière des en-têtes.
 */
async function lireUneZone(
  doc: SuperDocDocumentApi,
  section: { kind: 'section'; sectionId: string },
  cible: 'header' | 'footer',
  zone: CanvasHeaderFooterType,
): Promise<CanvasHeaderFooterZone> {
  const vide: CanvasHeaderFooterZone = {
    status: 'none',
    text: '',
    paragraphs: [],
    has_image: false,
    is_empty: true,
  };

  const resolve = operationDuDocument(doc, 'headerFooters', 'resolve');
  if (resolve === undefined) return vide;

  const statut = lireLeStatutDeSlot(await resolve({ target: slotDEnTete(section, cible, zone) }));
  // Un slot sans partie résolue n'a aucune story à lire : appeler `blocks.list`
  // dessus matérialiserait potentiellement une partie (`onWrite` par défaut) —
  // une LECTURE qui écrit serait le pire de tous les défauts possibles ici.
  if (statut === 'none') return vide;

  const story = storyDuSlot(section, cible, zone);
  const blocs = items(await doc.blocks?.list?.({ in: story, includeText: true, offset: 0, limit: MAX_HEADER_PARAGRAPHS }))
    .filter(isRecord);

  const listerLesImages = operationDuDocument(doc, 'images', 'list');
  const porteursDImage = listerLesImages === undefined
    ? new Set<string>()
    : blocsPorteursDImage(await listerLesImages({ in: story }));

  const paragraphes = blocs.map((bloc) => {
    const texte = texteBorneA(bloc.text ?? bloc.textPreview, MAX_HEADER_PARAGRAPH_LENGTH) ?? '';
    const alignement = alignementLisible(bloc.alignment);
    return { text: texte, ...(alignement === undefined ? {} : { alignment: alignement }) };
  });
  const concatene = paragraphes.map((paragraphe) => paragraphe.text).filter((texte) => texte.length > 0).join('\n');
  const aUneImage = blocs.some((bloc) => {
    const nodeId = stringValue(bloc.nodeId);
    return nodeId !== undefined && porteursDImage.has(nodeId);
  });

  return {
    status: statut,
    text: concatene.length > MAX_HEADER_ZONE_TEXT_LENGTH
      ? `${concatene.slice(0, MAX_HEADER_ZONE_TEXT_LENGTH - 1)}…`
      : concatene,
    paragraphs: paragraphes,
    has_image: aUneImage,
    // ⚠️ `is_empty` dit « cette zone ne porte AUCUN bloc », pas « je n'y ai lu
    // aucun texte ». La lecture d'un bloc peut être vide sans que la zone le
    // soit : un paragraphe qui ne porte qu'une image se relit `text:''`
    // (mesuré 2026-09-05), et rien ne garantit qu'il soit le seul cas. Juger
    // sur le seul texte fait alors annoncer « zone vide » sur une zone qui
    // peint quelque chose. Un bloc présent mais illisible reste un bloc.
    // (Contre-exemple utile, mesuré le 2026-10-05 : le pied de pagination du
    // gabarit rend `text:'Page  / '`, donc lui n'a jamais été concerné.)
    is_empty: paragraphes.length === 0 && !aUneImage,
  };
}

/**
 * LE lecteur des en-têtes et pieds de page — il n'y en a qu'un.
 *
 * ⚠️ **Lu SUR DEMANDE, jamais à chaque instantané** (voir l'appel dans
 * `superDocCanvasExecutor.executeInspect`). Contrairement aux révisions, aux
 * commentaires et aux notes — relus de toute façon pour alimenter `counts` —
 * ce domaine coûte des APPELS au fournisseur : une résolution, une liste de
 * blocs et une liste d'images par zone, soit jusqu'à dix-huit aller-retours par
 * section. Le servir à chaque inspection ralentirait toutes les autres.
 *
 * ⚠️ **Best-effort, comme `lireLesNotes`** : la famille `headerFooters.*`
 * n'existe qu'à partir de `superdoc@2.6.0`, et une lecture en échec ne doit pas
 * priver l'agent de TOUTE l'inspection. Le repli est un document sans section
 * lisible (`{ sections: [] }`), qui dit « je n'ai rien pu lire » et non « ce
 * document n'a pas d'en-tête » — la distinction est portée par
 * `INSPECT_DESCRIPTION`, le receipt ne pouvant pas la coder.
 */
export async function lireLesEnTetes(doc: SuperDocDocumentApi): Promise<CanvasHeadersFooters> {
  try {
    const listerLesSections = operationDuDocument(doc, 'sections', 'list');
    if (listerLesSections === undefined) return { sections: [] };

    const listees = items(await listerLesSections({}))
      .filter(isRecord)
      // Le contrat borne à 20 sections : au-delà, on tronque plutôt que de faire
      // rejeter le receipt entier. Un acte n'en a qu'une, un Word importé
      // rarement plus de quelques-unes.
      .slice(0, 20);

    const sections: CanvasHeaderFooterSection[] = [];
    for (const [rang, entree] of listees.entries()) {
      const section = adresseDeSection(entree);
      if (section === null) continue;
      const index = numberValue(entree.index) ?? rang;
      const [primaryH, firstH, evenH, primaryF, firstF, evenF] = await Promise.all([
        lireUneZone(doc, section, 'header', 'primary'),
        lireUneZone(doc, section, 'header', 'first_page'),
        lireUneZone(doc, section, 'header', 'even_pages'),
        lireUneZone(doc, section, 'footer', 'primary'),
        lireUneZone(doc, section, 'footer', 'first_page'),
        lireUneZone(doc, section, 'footer', 'even_pages'),
      ]);
      sections.push({
        section_index: index,
        different_first_page: booleenDeSection(entree.titlePage),
        odd_even: booleenDeSection(entree.oddEvenHeadersFooters),
        headers: { primary: primaryH, first_page: firstH, even_pages: evenH },
        footers: { primary: primaryF, first_page: firstF, even_pages: evenF },
      });
    }
    return { sections };
  } catch {
    return { sections: [] };
  }
}

export interface CanvasSnapshot {
  /**
   * Révision au sens du verrou optimiste — lue de `doc.info()`, la seule
   * surface que les mutations acceptent (#980). Marquée pour qu'aucune autre
   * « revision » du provider ne puisse s'y substituer.
   */
  revision: RevisionDeDocument;
  counts: { blocks: number; blocksTotalExact: boolean; trackedChanges: number; comments: number };
  blocks: CanvasSnapshotBlock[];
  /**
   * Les révisions suivies du document, lues UNE fois par snapshot (#994).
   *
   * `counts.trackedChanges` en est la longueur : il n'existe plus de chemin où
   * l'on compte les révisions sans pouvoir les nommer. Toute surface qui a
   * besoin d'identités avant/après lit ce tableau plutôt que de rappeler le
   * fournisseur — c'est la double lecture par action que #994 supprime.
   */
  trackedChanges: CanvasSnapshotRevision[];
  /**
   * Les commentaires du document, lus UNE fois par snapshot (#996).
   *
   * `counts.comments` en est la longueur — il n'existe plus de chemin où l'on
   * compte les commentaires sans pouvoir les nommer, exactement comme pour les
   * révisions depuis #994.
   *
   * ⚠️ **Fenêtre BORNÉE par le fournisseur** : `comments.list({})` rend une page
   * (`{limit:100, offset:0}` mesuré). Un commentaire absent de ce tableau n'est
   * donc pas une preuve d'inexistence — la doctrine « signal absent ≠ signal
   * négatif » s'applique aux postconditions qui s'appuient dessus.
   */
  comments: CanvasSnapshotComment[];
  /**
   * Les notes de bas de page du document, lues UNE fois par snapshot.
   *
   * ⚠️ **Aucun `counts.footnotes` ne l'accompagne, et c'est délibéré.**
   * `CanvasInspectReceiptSchema.counts` est `.strict()` et ne déclare que
   * `blocks / blocksTotalExact / trackedChanges / comments` : y ajouter une clé
   * ici ferait rejeter le receipt ENTIER, puisque `superDocCanvasExecutor` passe
   * `snapshot.counts` tel quel. Les notes n'ont donc PAS le signal d'appel que
   * les révisions et les commentaires possèdent — le modèle n'apprend qu'il y a
   * des notes qu'en demandant le domaine. Élargir `counts` est un changement de
   * contrat partagé, pas une retouche de lecture.
   *
   * ⚠️ **Cette liste dit ce que le document PORTE, pas ce que l'avocat VERRA** :
   * sous régime suivi, une note supprimée y reste jusqu'à l'acceptation de la
   * révision (mesuré 2026-08-26, §9.4 du vault). Sa longueur ne peut donc servir
   * de postcondition à une suppression.
   */
  footnotes: CanvasSnapshotFootnote[];
  /** Raw provider count is resolver-only and never exposed in a model receipt. */
  rawBlocksCount: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function numberValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function safeIntegerValue(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : undefined;
}

function items(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (!isRecord(value)) return [];
  if (Array.isArray(value.blocks)) return value.blocks;
  if (Array.isArray(value.items)) return value.items;
  return [];
}

function blockTotal(value: unknown): number | undefined {
  if (!isRecord(value)) return undefined;
  const direct = numberValue(value.blockCount) ?? numberValue(value.blocksCount) ?? numberValue(value.total);
  if (direct !== undefined) return direct;
  return isRecord(value.counts) ? numberValue(value.counts.blocks) : undefined;
}

function providerFailureCode(value: unknown): string {
  if (!isRecord(value)) return 'document_api_failure';
  const failure = isRecord(value.failure) ? value.failure : value;
  return stringValue(failure.code) ?? 'document_api_failure';
}

function normalizeBlock(
  raw: Record<string, unknown>,
  ordinal: number,
  augmentation?: AttributsStructurels,
): CanvasSnapshotBlock | null {
  const nodeId = stringValue(raw.nodeId) ?? stringValue(raw.blockId) ?? stringValue(raw.id);
  if (!nodeId) return null;

  // ⚠️ `text` GAGNE sur `textPreview`, et l'inverse était le défaut de #1008.
  // `textPreview` est le champ d'une LISTE D'INTERFACE — SuperDoc le coupe à
  // ~78 caractères — alors que `text` est le texte aplati complet, servi
  // précisément parce que `blocks.list` est appelé avec `includeText: true`
  // (voir `readCanvasSnapshot`). Brancher le champ d'UI sur le canal du modèle
  // rendait 43 blocs sur 69 tronqués par une ellipse.
  const fullText = stringValue(raw.text) ?? stringValue(raw.textPreview) ?? '';
  const modelText = fullText.slice(0, MAX_MODEL_TEXT_LENGTH);
  const styleId = stringValue(raw.styleId);
  const fontFamily = stringValue(raw.fontFamily);
  const fontSize = numberValue(raw.fontSize) ?? numberValue(raw.fontSizePt);
  const alignment = stringValue(raw.alignment);
  const nodeType = stringValue(raw.nodeType) ?? 'paragraph';
  // N'applique l'augmentation structurelle QUE si le type de nœud concorde
  // entre les deux lectures (`doc.blocks.list()` vs le corps SDM de
  // `doc.get()`) : un désaccord signale que l'alignement positionnel supposé
  // par `readCanvasSnapshot` ne tient plus pour ce document — mieux vaut
  // omettre le champ que le poser sur le mauvais bloc.
  const augmentationMatches = augmentation !== undefined && augmentation.nodeType.toLowerCase() === nodeType.toLowerCase();

  return {
    nodeId,
    ordinal,
    nodeType,
    fullText,
    text: modelText,
    ...(modelText.length < fullText.length ? { textTruncated: true as const } : {}),
    ...(numberValue(raw.headingLevel) === undefined ? {} : { headingLevel: numberValue(raw.headingLevel) }),
    ...(numberValue(raw.listLevel) === undefined ? {} : { listLevel: numberValue(raw.listLevel) }),
    ...(styleId === undefined && fontFamily === undefined && fontSize === undefined && alignment === undefined
      ? {}
      : { effectiveStyle: { ...(styleId === undefined ? {} : { styleId }), ...(fontFamily === undefined ? {} : { fontFamily }), ...(fontSize === undefined ? {} : { fontSize }), ...(alignment === undefined ? {} : { alignment }) } }),
    ...(augmentationMatches && augmentation.pageBreakBefore ? { pageBreakBefore: true } : {}),
    ...(augmentationMatches && augmentation.table ? { table: augmentation.table } : {}),
  };
}

/** Removes resolver-only text before a snapshot becomes a model tool result. */
export function toModelSafeCanvasBlocks(blocks: readonly CanvasSnapshotBlock[]): ModelSafeCanvasSnapshotBlock[] {
  return blocks.map(({ fullText: _fullText, ...block }) => block);
}

/**
 * MÉMOÏSATION DU CORPS SDM PAR RÉVISION — la seule lecture non fenêtrée.
 *
 * `doc.blocks.list()` est fenêtré ; `doc.get()` ne l'est pas (`SDGetInput` n'a
 * ni offset ni limite) et rend le document ENTIER. L'augmentation structurelle
 * qu'on en tire — saut de page, largeur de table — est un champ diagnostique,
 * mais son coût est la sérialisation SDM de tout le document, payée à CHAQUE
 * inspect. Sur un acte long, une session d'agent qui inspecte entre chaque
 * mutation, ou une sonde qui boucle en `expect.poll`, ce coût se répète sans
 * que rien ne change dans le document.
 *
 * La révision EST l'identité du contenu : deux inspects à la même révision
 * décrivent le même document, donc portent le même corps SDM. On la lit déjà
 * (`doc.info()`), juste avant.
 *
 * Clé `WeakMap` sur le `doc` lui-même, pas sur la révision seule : deux
 * documents peuvent porter la même chaîne de révision (un canvas fraîchement
 * monté repart de la révision initiale du gabarit), et un cache global les
 * confondrait — l'agent lirait les tables d'un acte dans un autre. La `WeakMap`
 * libère aussi l'entrée avec le document démonté, sans code de purge.
 */
const corpsSdmParDocument = new WeakMap<object, { revision: string; attributs: AttributsStructurels[] }>();

async function lireLesAttributsStructurelsMemoises(
  doc: SuperDocDocumentApi,
  revision: string,
): Promise<AttributsStructurels[]> {
  // Une révision illisible ne peut pas servir de clé : la mémoïser ferait
  // resservir un corps périmé à tous les inspects suivants du même document.
  // On relit alors, ce qui est l'ancien comportement.
  if (revision === 'unknown') return lireLesAttributsStructurels(await lireLeCorpsSdmOuRien(doc));
  const enCache = corpsSdmParDocument.get(doc);
  if (enCache && enCache.revision === revision) return enCache.attributs;
  const attributs = lireLesAttributsStructurels(await lireLeCorpsSdmOuRien(doc));
  corpsSdmParDocument.set(doc, { revision, attributs });
  return attributs;
}

/** Documents dont `doc.get()` a déjà levé : le refus n'est journalisé qu'une fois par document. */
const corpsSdmIllisibleDejaJournalise = new WeakSet<object>();

/**
 * Le corps SDM, ou aucun nœud quand `doc.get()` LÈVE.
 *
 * `doc.get()` lève sur certains Word : en 2.15.1, une zone de texte qui contient
 * une image en ligne (`UTF-8 offset must be an integer in […]`, signalement du
 * 2026-10-09). L'inspection n'en tire que des attributs d'appoint (saut de page
 * avant, largeurs de table) : elle rend les blocs sans eux plutôt que rien.
 *
 * ⚠️ Réservé à cette augmentation. Les lectures qui tirent leurs PARAGRAPHES de
 * `lireLeCorpsSdm` (`canvasBridge.ts`) doivent continuer de lever : un corps
 * vide s'y lirait comme un document vide.
 */
async function lireLeCorpsSdmOuRien(doc: SuperDocDocumentApi): ReturnType<typeof lireLeCorpsSdm> {
  try {
    return await lireLeCorpsSdm(doc);
  } catch (erreur) {
    if (!corpsSdmIllisibleDejaJournalise.has(doc)) {
      corpsSdmIllisibleDejaJournalise.add(doc);
      console.warn(
        `[canvas] editor.doc.get({}) threw (${erreur instanceof Error ? `${erreur.name}: ${erreur.message}` : String(erreur)}); `
        + 'canvas_inspect publishes blocks without their structural attributes (page break before, table widths) for this document.',
      );
    }
    return [];
  }
}

async function lireLesCommentairesDuDocument(doc: SuperDocDocumentApi): Promise<CanvasSnapshotComment[]> {
  try {
    return lireLesCommentaires(await doc.comments?.list?.({}));
  } catch {
    return [];
  }
}

/**
 * La lecture des notes, dans le même contrat best-effort que les commentaires.
 *
 * ⚠️ **La surface est atteinte par NARROWING, pas par `doc.footnotes`** :
 * `SuperDocDocumentApi` ne déclare pas encore la famille `footnotes.*` — elle
 * est déclarée avec les EXÉCUTEURS de notes, qui ont besoin d'`insert`,
 * `update` et `remove` en plus de `list`. Écrire ici une seconde déclaration
 * partielle du même objet ferait diverger deux descriptions du fournisseur.
 * Le `isRecord` reste valable une fois la famille déclarée : cette lecture n'a
 * pas à changer quand elle le sera.
 *
 * ⚠️ **Best-effort, et c'est le même arbitrage qu'en #996** : un `footnotes.list`
 * en échec — ou simplement absent, sur un runtime plus ancien que
 * `superdoc@2.6.0` — ne doit pas priver l'agent de TOUTE la lecture du document.
 * Le repli est le tableau vide, donc une inspection sans notes : un vide par
 * défaut de lecture est indiscernable d'un vrai « aucune note », limite assumée
 * parce que l'alternative (perdre les blocs, le jeton de révision et le reste du
 * receipt) est strictement pire.
 */
async function lireLesNotesDuDocument(doc: SuperDocDocumentApi): Promise<CanvasSnapshotFootnote[]> {
  try {
    // ⚠️ Passe par `operationDuDocument`, comme toute lecture de famille. La
    // garde `isRecord` qui vivait ici ne marchait que parce que `doc.footnotes`
    // se trouve être un OBJET : le jour où le fournisseur en fait un proxy
    // appelable — comme `sections`, `headerFooters` et `images` le sont DÉJÀ
    // (mesuré le 2026-09-05) — elle aurait rendu « aucune note » sur un document
    // qui en porte, sans le moindre signal.
    const lister = operationDuDocument(doc, 'footnotes', 'list');
    return lister === undefined ? [] : lireLesNotes(await lister({}));
  } catch {
    return [];
  }
}

/**
 * Reads a bounded, model-safe window. Provider ordinals are deliberately ignored:
 * SuperDoc exposes them as zero-based on some document shapes, while the agent
 * contract only ever publishes absolute one-based ordinals.
 */
export async function readCanvasSnapshot(
  doc: SuperDocDocumentApi,
  window: { offset: number; limit: number },
): Promise<CanvasSnapshot> {
  // Chaque appel du Document API est ATTENDU (#939) : en 1.44.1 il rend une
  // valeur et l'attente est un simple tour de microtâche, en V2 il rendra une
  // promesse. Lire sans attendre produirait alors un `Promise` là où le code
  // attend un objet — et une lecture silencieusement vide, pas une erreur.
  const { info, revision: revisionLue } = await lireLInfoDuDocument(doc);
  const listed = await doc.blocks?.list?.({ offset: window.offset, limit: window.limit, includeText: true });
  if (isRecord(listed) && listed.success === false) {
    throw new CanvasSnapshotReadError(providerFailureCode(listed));
  }
  const rawBlocks = items(listed);
  // Le corps SDM n'est PAS fenêtré (`doc.get()` rend le document entier) :
  // l'entrée `window.offset + index` y décrit le même bloc top-level que
  // `rawBlocks[index]`, que `normalizeBlock` re-contrôle par le type de nœud.
  // Mémoïsé par révision — voir `lireLesAttributsStructurelsMemoises`.
  // ⚠️ `REVISION_ABSENTE` est une SENTINELLE, pas une révision — et elle sort
  // d'ici par deux chemins : clé de mémoïsation ci-dessous, ET champ
  // `snapshot.revision` publié plus bas. Elle est marquée, donc elle PEUT
  // atteindre une fence ; ce qui la rend sûre n'est pas qu'on l'en empêche,
  // c'est que le provider la refuse (`REVISION_MISMATCH`) — un refus, jamais
  // une mutation au mauvais endroit. Même énoncé qu'à sa définition
  // (`fencesDeMutation.ts`) : les deux commentaires doivent rester d'accord.
  const revisionCourante = revisionLue ?? REVISION_ABSENTE;
  const structuralAttrs = await lireLesAttributsStructurelsMemoises(doc, revisionCourante);
  const blocks = rawBlocks
    .filter(isRecord)
    .map((raw, index) => normalizeBlock(raw, window.offset + index + 1, structuralAttrs[window.offset + index]))
    .filter((block): block is CanvasSnapshotBlock => block !== null);
  const providerTotal = blockTotal(info) ?? blockTotal(listed);
  const normalizedBoundedTotal = window.offset + blocks.length;
  const rawBoundedTotal = window.offset + rawBlocks.length;
  // Invalid provider blocks are omitted from the model snapshot, but they still
  // prove this page was full; only raw page length can establish end-of-document.
  const blocksTotalExact = providerTotal !== undefined || rawBlocks.length < window.limit;
  // UNE lecture des révisions par snapshot, dont sortent à la fois le compte
  // publié et les identités décidables (#994).
  const trackedChanges = lireLesRevisionsSuivies(await doc.trackChanges?.list?.({}));
  // UNE lecture des commentaires par snapshot, dont sortent à la fois le compte
  // publié et les identifiants décidables (#996).
  //
  // ⚠️ **Best-effort, et c'est délibéré** : une liste de commentaires en échec ne
  // doit pas priver l'agent de TOUTE la lecture du document — c'est déjà la
  // doctrine de `lectureSdm` pour les ancres de commentaire. Le repli est le
  // tableau vide, donc `counts.comments = 0` : un zéro par défaut de lecture est
  // indiscernable d'un vrai zéro, limite assumée ici parce que l'alternative
  // (perdre l'inspection entière) est strictement pire.
  const comments = await lireLesCommentairesDuDocument(doc);
  // UNE lecture des notes par snapshot, dont sortent les `noteId` sans lesquels
  // `edit_footnote` et `delete_footnote` seraient inappelables.
  //
  // ⚠️ Best-effort au même titre que les commentaires ci-dessus, et pour une
  // raison de plus : la famille `footnotes.*` n'existe qu'à partir de
  // `superdoc@2.6.0` (§9.1 du vault, relevé du 2026-08-26). Sur un runtime plus
  // ancien la surface est simplement absente, et une absence de surface ne doit
  // pas se lire comme un document illisible.
  const footnotes = await lireLesNotesDuDocument(doc);

  return {
    revision: revisionCourante,
    counts: {
      // Keep `blocks` document-wide when SuperDoc reports a total. Some tested
      // provider shapes do not; then the highest observed ordinal is an explicit
      // lower bound rather than the misleading page-local block count.
      blocks: providerTotal ?? (blocksTotalExact ? rawBoundedTotal : normalizedBoundedTotal),
      blocksTotalExact,
      trackedChanges: trackedChanges.length,
      comments: comments.length,
    },
    blocks,
    trackedChanges,
    comments,
    footnotes,
    rawBlocksCount: rawBlocks.length,
  };
}
